import type { TokenResponse } from '@et/shared';
import { useEffect, useSyncExternalStore } from 'react';
import { BrowserRouter, NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { api, session } from './api/client';
import { connectWs } from './api/ws-client';
import { EngineChip, EngineControls, ModeBadge, WsChip } from './components/EngineControls';
import { useLang, useT } from './lib/i18n';
import { useToasts } from './lib/toast';
import { Credentials } from './pages/Credentials';
import { Dashboard } from './pages/Dashboard';
import { Login } from './pages/Login';
import { News } from './pages/News';
import { Reports } from './pages/Reports';
import { Backtest } from './pages/Backtest';
import { Risk } from './pages/Risk';
import { Sentiment } from './pages/Sentiment';
import { Audit, Settings } from './pages/Settings';
import { Strategies } from './pages/Strategies';
import { Trades } from './pages/Trades';
import { useLive } from './stores/live';

const NAV: [string, string][] = [
  ['/', 'Dashboard'],
  ['/reports', 'Reports'],
  ['/backtest', 'Backtest'],
  ['/trades', 'Trades & signals'],
  ['/strategies', 'Strategies'],
  ['/risk', 'Risk & universe'],
  ['/news', 'News sources'],
  ['/sentiment', 'Sentiment'],
  ['/credentials', 'Credentials'],
  ['/settings', 'Settings'],
  ['/audit', 'Audit'],
];

/** Refreshes the JWT a few minutes before it expires, so an open dashboard never logs out. */
function useTokenRefresh(): void {
  useEffect(() => {
    const id = window.setInterval(() => {
      const exp = session.expiresAt();
      if (exp !== null && exp - Date.now() < 5 * 60_000) {
        void api.post<TokenResponse>('/auth/refresh').then(session.set, () => undefined);
      }
    }, 60_000);
    return () => { window.clearInterval(id); };
  }, []);
}

function Toasts(): JSX.Element {
  const toasts = useToasts((s) => s.toasts);
  return <div className="toasts" aria-live="polite">{toasts.map((t) => <div key={t.id} className={`toast ${t.kind}`}>{t.text}</div>)}</div>;
}

function LangSwitch(): JSX.Element {
  const { lang, setLang } = useLang();
  return (
    <div className="row" style={{ padding: '12px 10px 0' }} role="group" aria-label="Language / Dil">
      <button className={`small ${lang === 'tr' ? 'primary' : ''}`} onClick={() => { setLang('tr'); }}>TR</button>
      <button className={`small ${lang === 'en' ? 'primary' : ''}`} onClick={() => { setLang('en'); }}>EN</button>
    </div>
  );
}

function Shell(): JSX.Element {
  useTokenRefresh();
  const t = useT();
  useEffect(() => {
    const live = useLive.getState();
    void live.loadAll().catch(() => undefined);
    return connectWs({
      onMessage: (m) => { useLive.getState().apply(m); },
      onStatus: (s) => { useLive.getState().setWs(s); },
      onReconnect: () => void useLive.getState().loadAll().catch(() => undefined),
    });
  }, []);

  return (
    <div className="shell">
      <nav className="nav">
        <div className="brand">ET-TradeBot</div>
        {NAV.map(([to, label]) => <NavLink key={to} to={to} end={to === '/'}>{t(label)}</NavLink>)}
        <a href="#" onClick={(e) => { e.preventDefault(); session.clear(); }} style={{ marginTop: 16 }}>{t('Sign out')}</a>
        <LangSwitch />
      </nav>
      <main className="main">
        <div className="topbar">
          <ModeBadge />
          <EngineChip />
          <WsChip />
          <span className="spacer" />
          <EngineControls />
        </div>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/backtest" element={<Backtest />} />
          <Route path="/trades" element={<Trades />} />
          <Route path="/strategies" element={<Strategies />} />
          <Route path="/risk" element={<Risk />} />
          <Route path="/news" element={<News />} />
          <Route path="/sentiment" element={<Sentiment />} />
          <Route path="/credentials" element={<Credentials />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

export function App(): JSX.Element {
  const lang = useLang((s) => s.lang);
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const token = useSyncExternalStore(session.subscribe, session.token);
  return (
    <BrowserRouter>
      {token ? <Shell /> : <Login />}
      <Toasts />
    </BrowserRouter>
  );
}
